import { useLayoutEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectIsStateParticipantBarLeader,
  selectMaxParticipantBarSlots,
  selectParticipantBarVisibleClassUserIds,
} from "store/selectors/pubsub-virtual-class-state";
import {
  selectClassUsersFromParticipantBarInputSorted,
  selectParticipantBarInputSource,
} from "store/selectors/virtual-class-users-sorting";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";
import { useAv, useAvPubsubMessaging } from "utils/av";

// Keeps the visible participants list in sync with the calculated list.
export function useLocalParticipantBarUpdateObserver() {
  const dispatch = useAppDispatch();
  const { isInVirtualClass } = useAv();
  const isStateParticipantBarLeader = useAppSelector(selectIsStateParticipantBarLeader);
  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();
  const prevSourceRef = useRef<string | null>(null);
  const maxSlots = useAppSelector(selectMaxParticipantBarSlots);
  const currentParticipantBarVisibleIds = useAppSelector(selectParticipantBarVisibleClassUserIds);
  const participantBarInputSource = useAppSelector(selectParticipantBarInputSource).source;
  const displayed = useAppSelector(selectClassUsersFromParticipantBarInputSorted);

  useLayoutEffect(() => {
    if (!isInVirtualClass && prevSourceRef.current !== null) {
      prevSourceRef.current = null;
      return undefined;
    }

    // If the source changed (for instance, in breakouts), we assume the visible participants list is no longer valid and reset it.
    const sourceChanged = prevSourceRef.current !== participantBarInputSource;
    const prevSource = prevSourceRef.current;
    prevSourceRef.current = participantBarInputSource;
    if (sourceChanged && prevSource !== null) {
      dispatch(PubsubVirtualClassStateActions.setParticipantBarVisibleClassUserIds([]));
      if (isStateParticipantBarLeader) {
        sendPubsubVirtualClassStateMessage({
          action: "clear-pinned-class-users",
        });
      }
      return;
    }

    const nextParticipantBarVisibleIds = displayed.slice(0, maxSlots).map((u) => u.id);
    if (
      nextParticipantBarVisibleIds.length === currentParticipantBarVisibleIds.length &&
      nextParticipantBarVisibleIds.every(
        (id, index) => id === currentParticipantBarVisibleIds[index],
      )
    ) {
      return;
    }
    dispatch(
      PubsubVirtualClassStateActions.setParticipantBarVisibleClassUserIds(
        nextParticipantBarVisibleIds,
      ),
    );
  }, [
    isInVirtualClass,
    dispatch,
    isStateParticipantBarLeader,
    sendPubsubVirtualClassStateMessage,
    maxSlots,
    currentParticipantBarVisibleIds,
    participantBarInputSource,
    displayed,
  ]);
}
